import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { MapContainer, TileLayer, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { CommunityLocation } from '../types/api';
import Sidebar from './Sidebar';
import MapOverlay from './MapOverlay';
import MapMarker from './MapMarker';
import SidebarToggle from './SidebarToggle';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface GeospatialMapProps {
  locations: CommunityLocation[];
  loading?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  hasMoreData?: boolean;
  availableCarelevels?: string[];
  currentCareLevel?: string;
  onCareLevelChange?: (careLevel: string) => void;
}

interface GeospatialMapRef {
  centerOnCommunity: (community: CommunityLocation) => void;
}

const GeospatialMap = forwardRef<GeospatialMapRef, GeospatialMapProps>(({
  locations,
  loading = false,
  loadingMore = false,
  onLoadMore,
  hasMoreData = true,
  availableCarelevels = [],
  currentCareLevel = '',
  onCareLevelChange
}, ref) => {
  // Default center (approximate center of US)
  const defaultCenter: [number, number] = [39.8283, -98.5795];
  const defaultZoom = 4;
  const [visibleLocations, setVisibleLocations] = useState<CommunityLocation[]>([]);
  const [filteredCommunity, setFilteredCommunity] = useState<CommunityLocation | null>(null);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [hoveredCommunityId, setHoveredCommunityId] = useState<string | null>(null);
  const [lastZoomLevel, setLastZoomLevel] = useState<number>(defaultZoom);
  const mapRef = useRef<L.Map | null>(null);

  // Function to update visible locations based on map bounds
  const updateVisibleLocations = useCallback(() => {
    if (!mapRef.current) return;
    
    const map = mapRef.current;
    const bounds = map.getBounds();
    
    // If a community is filtered/isolated, show only that community
    if (filteredCommunity) {
      setVisibleLocations([filteredCommunity]);
      return;
    }
    
    const visible = locations.filter(location => {
      return bounds.contains([location.latitude, location.longitude]);
    });
    
    setVisibleLocations(visible);
  }, [locations, filteredCommunity]);

  // Component to handle map events for infinite scrolling and sidebar updates
  const MapEventHandler = () => {
    useMapEvents({
      moveend: () => {
        // Update visible locations for sidebar
        updateVisibleLocations();
        
        // Only trigger load more when user pans to edge of current data
        // Check if current view shows less than a threshold of available data
        if (onLoadMore && hasMoreData && !loadingMore && mapRef.current) {
          const map = mapRef.current;
          const zoom = map.getZoom();
          const bounds = map.getBounds();
          
          // Count how many locations are visible in current view
          const visibleCount = locations.filter(location => {
            return bounds.contains([location.latitude, location.longitude]);
          }).length;
          
          // Only load more if:
          // 1. Zoomed in enough to see communities (>= 8)
          // 2. Less than 50 communities visible (suggesting we're at edge of data)
          // 3. User has moved significantly (to avoid loading on small adjustments)
          if (zoom >= 8 && visibleCount < 50) {
            setTimeout(() => {
              if (onLoadMore) onLoadMore();
            }, 1000); // Longer debounce for move events
          }
        }
      },
      zoomstart: () => {
        // Store the current zoom level before zoom starts
        if (mapRef.current) {
          setLastZoomLevel(mapRef.current.getZoom());
        }
      },
      zoomend: () => {
        // Update visible locations for sidebar
        updateVisibleLocations();
        
        // Only load more data when zooming IN, not when zooming OUT
        if (onLoadMore && hasMoreData && !loadingMore && mapRef.current) {
          const map = mapRef.current;
          const currentZoom = map.getZoom();
          
          // Only trigger load more if:
          // 1. User zoomed IN (current zoom > last zoom)
          // 2. Zoom level is high enough (>= 10)
          if (currentZoom > lastZoomLevel && currentZoom >= 10) {
            setTimeout(() => {
              if (onLoadMore) onLoadMore();
            }, 500);
          }
          
          // Update the last zoom level for next comparison
          setLastZoomLevel(currentZoom);
        }
      }
    });
    return null;
  };

  // Update visible locations when locations change
  useEffect(() => {
    if (locations.length > 0) {
      setTimeout(updateVisibleLocations, 100);
    }
  }, [locations, updateVisibleLocations]);

  const handleMarkerClick = async (location: CommunityLocation) => {
    console.log('Location clicked:', location);
    
    if (mapRef.current) {
      // Center and zoom in on the clicked marker
      mapRef.current.setView([location.latitude, location.longitude], 14);
      
      // Isolate this community in the sidebar
      setFilteredCommunity(location);
      
      // Ensure sidebar is visible
      setSidebarVisible(true);
    }
  };


  const handleSidebarCommunityClick = (community: CommunityLocation) => {
    if (mapRef.current) {
      // Center the map on the selected community
      mapRef.current.setView([community.latitude, community.longitude], 12);
      // Clear any filtering and zoom to the community
      setFilteredCommunity(null);
      handleMarkerClick(community);
    }
  };

  const handleSearchResultClick = (result: {
    community_name: string;
    latitude: number;
    longitude: number;
    corporation_name?: string;
  }) => {
    if (mapRef.current) {
      // Center the map on the search result
      mapRef.current.setView([result.latitude, result.longitude], 14);
      
      // Find the matching community in our locations
      const matchingCommunity = locations.find(loc => 
        loc.community_name === result.community_name &&
        Math.abs(loc.latitude - result.latitude) < 0.001 &&
        Math.abs(loc.longitude - result.longitude) < 0.001
      );
      
      if (matchingCommunity) {
        // Isolate this community in the sidebar
        setFilteredCommunity(matchingCommunity);
      }
      
      // Ensure sidebar is visible
      setSidebarVisible(true);
    }
  };

  const handleClearFilter = () => {
    setFilteredCommunity(null);
    if (mapRef.current) {
    const currentZoom = mapRef.current.getZoom();
    mapRef.current.setZoom(currentZoom - 3);
  }
    setTimeout(updateVisibleLocations, 100);
  };

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    centerOnCommunity: (community: CommunityLocation) => {
      if (mapRef.current) {
        // Center the map on the community
        mapRef.current.setView([community.latitude, community.longitude], 12);
        // Load the community details
        handleMarkerClick(community);
      }
    }
  }), []);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: '16px', fontSize: '18px' }}>Loading map...</div>
          <div style={{ fontSize: '14px', color: '#666' }}>Please wait while we load the locations</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex' }}>
      {/* Sidebar */}
      <Sidebar
        visible={sidebarVisible}
        visibleLocations={visibleLocations}
        totalLocations={locations.length}
        hoveredCommunityId={hoveredCommunityId}
        filteredCommunity={filteredCommunity}
        onClose={() => setSidebarVisible(false)}
        onCommunityClick={handleSidebarCommunityClick}
        onCommunityHover={setHoveredCommunityId}
        onClearFilter={handleClearFilter}
        onSearchResultClick={handleSearchResultClick}
        availableCarelevels={availableCarelevels}
        currentCareLevel={currentCareLevel}
        onCareLevelChange={onCareLevelChange}
      />
      
      {/* Toggle Sidebar Button */}
      <SidebarToggle
        visible={sidebarVisible}
        onToggle={() => setSidebarVisible(true)}
      />

      <div style={{ flex: 1, position: 'relative' }}>
        <MapContainer
          center={defaultCenter}
          zoom={defaultZoom}
          style={{ width: '100%', height: '100%' }}
          scrollWheelZoom={true}
          ref={mapRef}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          <MapEventHandler />
          
          {locations.map((location) => {
            // Use a more robust comparison that includes multiple identifying fields
            const locationIdentifier = `${location.community_id}-${location.community_name}-${location.latitude}-${location.longitude}`;
            const isHighlighted = hoveredCommunityId === location.community_id;
            const isDimmed = hoveredCommunityId !== null && hoveredCommunityId !== location.community_id;
            
            return (
              <MapMarker
                key={locationIdentifier}
                location={location}
                isHighlighted={isHighlighted}
                isDimmed={isDimmed}
                onClick={handleMarkerClick}
              />
            );
          })}
        </MapContainer>
        
     </div>
    </div>
  );
});

export default GeospatialMap;
